/**
 * SoleadLab 2.0 - Overlay Positioning Markers
 *
 * When a PNG/PDF overlay is clicked on the map, this hook creates
 * draggable Mapbox markers:
 * - 4 corner markers (white squares, magenta border) for free-form warping
 * - 4 side midpoint markers (blue rectangles) for edge stretching
 * - 4 scale markers (teal circles, outside corners) for proportional scaling
 * - 1 center marker (white circle, magenta border) for panning
 * - 1 rotation handle (green circle with dashed line) for rotating
 *
 * Matches original editor overlay handle system.
 * Clicking elsewhere on the map hides the markers.
 */

import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useMapStore, useProjectStore } from '../../stores';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getCenter(coords: number[][]): [number, number] {
  const sx = coords[0][0] + coords[1][0] + coords[2][0] + coords[3][0];
  const sy = coords[0][1] + coords[1][1] + coords[2][1] + coords[3][1];
  return [sx / 4, sy / 4];
}

function getMidpoint(p1: number[], p2: number[]): [number, number] {
  return [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
}

/** Ray-casting point-in-polygon test */
function pointInPolygon(px: number, py: number, polygon: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

// ─── Marker element factories (matching original editor CSS) ────────────────

/** Corner handle: white square, magenta border */
function createCornerHandle(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:14px; height:14px; background:#ffffff; border:2px solid #ff00ff; border-radius:2px; box-shadow:0 0 6px rgba(0,0,0,0.25); cursor:pointer;';
  el.title = 'Drag to warp corner';
  return el;
}

/** Side handle: blue rectangle, horizontal or vertical */
function createSideHandle(isHorizontal: boolean): HTMLDivElement {
  const el = document.createElement('div');
  if (isHorizontal) {
    el.style.cssText =
      'width:20px; height:10px; background:#3b82f6; border:2px solid #1d4ed8; border-radius:3px; box-shadow:0 0 4px rgba(0,0,0,0.3); cursor:ns-resize;';
    el.title = 'Drag to stretch vertically';
  } else {
    el.style.cssText =
      'width:10px; height:20px; background:#3b82f6; border:2px solid #1d4ed8; border-radius:3px; box-shadow:0 0 4px rgba(0,0,0,0.3); cursor:ew-resize;';
    el.title = 'Drag to stretch horizontally';
  }
  return el;
}

/** Scale handle: teal circle positioned outside each corner */
function createScaleHandle(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:14px; height:14px; background:#13d6c6; border:2px solid #fff; border-radius:50%; box-shadow:0 2px 4px rgba(0,0,0,0.3); cursor:pointer;';
  el.title = 'Drag to scale (keeps proportions)';
  return el;
}

/** Center handle: white circle, magenta border */
function createCenterHandle(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:16px; height:16px; background:#ffffff; border:2px solid #ff00ff; border-radius:50%; box-shadow:0 0 6px rgba(0,0,0,0.25); cursor:move;';
  el.title = 'Drag to move overlay';
  return el;
}

/** Rotation handle: green circle with ↻ icon */
function createRotationHandle(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    'width:20px; height:20px; background:#10b981; border:3px solid #fff; border-radius:50%; cursor:grab; box-shadow:0 2px 6px rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center; font-size:12px; color:#fff;';
  el.textContent = '↻';
  el.title = 'Drag to rotate';
  return el;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export default function useOverlayMarkers() {
  const activeOverlayId = useMapStore((s) => s.activeOverlayId);
  const overlayAdjustMode = useMapStore((s) => s.overlayAdjustMode);
  const isMapReady = useMapStore((s) => s.isMapReady);
  // F-02E-2C: share viewers must never enter overlay edit mode.
  // Skipping both the click-select effect and the marker-render effect
  // means clicking a polygon does nothing (no activeOverlayId set,
  // no corner/side/rotate handles drawn). Authenticated callers are
  // unchanged.
  const isReadOnly = useIsReadOnlyViewer();

  // Refs for cleanup
  const cornerMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const sideMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const scaleMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const centerMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const rotationMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const rotationLineRef = useRef<boolean>(false);
  const isDraggingRef = useRef(false);

  // ── Effect 1: Map click handler — select/deselect overlays ──────────────
  useEffect(() => {
    if (!isMapReady) return;
    if (isReadOnly) return;
    const map = useMapStore.getState().map;
    if (!map) return;

    const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
      if (useMapStore.getState().overlayEditMode) return;
      if (isDraggingRef.current) return;

      // If the click hit a MapboxDraw feature (indication/photo), don't activate overlay.
      // Indications live on gl-draw layers; query a small area around the click point.
      const drawFeatures = map.queryRenderedFeatures(e.point, {
        layers: map.getStyle()?.layers
          ?.filter((l) => l.id.startsWith('gl-draw-'))
          .map((l) => l.id) || [],
      });
      if (drawFeatures.length > 0) return;

      const clickLng = e.lngLat.lng;
      const clickLat = e.lngLat.lat;

      const overlays = useProjectStore.getState().data.overlays;
      let clickedId: string | null = null;

      for (const overlay of overlays) {
        if (!overlay.visible) continue;
        if (pointInPolygon(clickLng, clickLat, overlay.bounds)) {
          clickedId = overlay.id;
          break;
        }
      }

      const currentActive = useMapStore.getState().activeOverlayId;

      // SELECTION only. Placement-adjust (handles + tiles→preview swap) is
      // entered exclusively from the Plans ticket's Adjust button — a map
      // click must never flip a baked plan into preview: misclicks while
      // editing labels over the sheet did exactly that (owner-reported).
      if (clickedId) {
        if (currentActive !== clickedId) {
          // setActiveOverlay also drops adjust mode when switching overlays.
          useMapStore.getState().setActiveOverlay(clickedId);
        }
      } else {
        if (useMapStore.getState().overlayAdjustMode) {
          // Click-away ends the adjust session, keeping the selection.
          useMapStore.getState().exitOverlayAdjustMode();
        } else if (currentActive) {
          // Only clear if the click wasn't on a manual orthophoto
          // (let the ortho click handler manage its own state)
          useMapStore.getState().setActiveOverlay(null);
        }
      }
    };

    map.on('click', handleMapClick);
    return () => { map.off('click', handleMapClick); };
  }, [isMapReady, isReadOnly]);

  // ── Effect 2: Create/remove markers based on activeOverlayId ────────────
  useEffect(() => {
    const map = useMapStore.getState().map;
    if (!map) return;
    // F-02E-2C: never render edit-handle markers for share viewers, even
    // if activeOverlayId is somehow set (e.g. via a stale store flip
    // from a prior owner session in the same tab).
    if (isReadOnly) return;

    const cleanup = () => {
      cornerMarkersRef.current.forEach((m) => { try { m.remove(); } catch (_e) { /* */ } });
      cornerMarkersRef.current = [];

      sideMarkersRef.current.forEach((m) => { try { m.remove(); } catch (_e) { /* */ } });
      sideMarkersRef.current = [];

      scaleMarkersRef.current.forEach((m) => { try { m.remove(); } catch (_e) { /* */ } });
      scaleMarkersRef.current = [];

      if (centerMarkerRef.current) {
        try { centerMarkerRef.current.remove(); } catch (_e) { /* */ }
        centerMarkerRef.current = null;
      }

      if (rotationMarkerRef.current) {
        try { rotationMarkerRef.current.remove(); } catch (_e) { /* */ }
        rotationMarkerRef.current = null;
      }

      if (rotationLineRef.current) {
        try { if (map.getLayer('overlay-rotation-line-layer')) map.removeLayer('overlay-rotation-line-layer'); } catch (_e) { /* */ }
        try { if (map.getSource('overlay-rotation-line-source')) map.removeSource('overlay-rotation-line-source'); } catch (_e) { /* */ }
        rotationLineRef.current = false;
      }
    };

    cleanup();

    // Handles exist only in adjust mode — selection alone renders nothing.
    if (!activeOverlayId || !overlayAdjustMode) return cleanup;

    const overlay = useProjectStore.getState().data.overlays.find((o) => o.id === activeOverlayId);
    if (!overlay) return cleanup;

    // Local mutable copy of coordinates
    const coords = overlay.bounds.map((c) => [...c]) as [number, number][];

    // Push coordinate changes to Mapbox image source in real-time.
    // MapCanvas renders the selected overlay as an image source even when it
    // has ready tiles (tile sources can't follow drags); the typeof guard
    // covers the brief async window while that swap is still in flight.
    const syncToMapSource = () => {
      const source = map.getSource(`overlay-${activeOverlayId}`) as mapboxgl.ImageSource | undefined;
      if (source && typeof (source as any).setCoordinates === 'function') {
        (source as any).setCoordinates(coords);
      }
    };

    // Reposition all markers to current coords
    const sideEdges = [[0, 1], [1, 2], [2, 3], [3, 0]];

    // Scale handle position: 15% beyond each corner on the diagonal from center
    const getScaleHandlePos = (cornerIdx: number): [number, number] => {
      const c = getCenter(coords);
      const dx = coords[cornerIdx][0] - c[0];
      const dy = coords[cornerIdx][1] - c[1];
      return [coords[cornerIdx][0] + dx * 0.15, coords[cornerIdx][1] + dy * 0.15];
    };

    // Rotation handle position: 30% beyond the top edge midpoint, on the line from center
    const getRotationHandlePos = (): [number, number] => {
      const topMid = getMidpoint(coords[0], coords[1]);
      const center = getCenter(coords);
      const dy = Math.abs(coords[0][1] - coords[3][1]);
      const dx = Math.abs(coords[0][0] - coords[3][0]);
      const height = Math.sqrt(dy * dy + dx * dx);
      const dirX = topMid[0] - center[0];
      const dirY = topMid[1] - center[1];
      const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);
      return dirLen > 0
        ? [topMid[0] + (dirX / dirLen) * height * 0.3, topMid[1] + (dirY / dirLen) * height * 0.3]
        : [topMid[0], topMid[1] + height * 0.3];
    };

    const updateRotationHandlePosition = () => {
      const handlePos = getRotationHandlePos();
      if (rotationMarkerRef.current) {
        rotationMarkerRef.current.setLngLat(handlePos);
      }
      const lineSource = map.getSource('overlay-rotation-line-source') as mapboxgl.GeoJSONSource | undefined;
      if (lineSource) {
        lineSource.setData({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [getCenter(coords), handlePos] },
          properties: {},
        });
      }
    };

    const repositionAll = () => {
      for (let i = 0; i < cornerMarkersRef.current.length; i++) {
        cornerMarkersRef.current[i].setLngLat(coords[i] as [number, number]);
      }
      for (let i = 0; i < sideMarkersRef.current.length; i++) {
        const mid = getMidpoint(coords[sideEdges[i][0]], coords[sideEdges[i][1]]);
        sideMarkersRef.current[i].setLngLat(mid);
      }
      for (let i = 0; i < scaleMarkersRef.current.length; i++) {
        scaleMarkersRef.current[i].setLngLat(getScaleHandlePos(i));
      }
      if (centerMarkerRef.current) {
        centerMarkerRef.current.setLngLat(getCenter(coords));
      }
      updateRotationHandlePosition();
    };

    // Flush coords to project store (on dragend)
    const flushToStore = () => {
      const typedCoords = coords as [[number, number], [number, number], [number, number], [number, number]];
      useProjectStore.getState().updateOverlay(activeOverlayId, {
        bounds: typedCoords,
      });
    };

    // ═══ 1. CORNER MARKERS (4) — free-form corner warping ════════════════
    for (let i = 0; i < 4; i++) {
      const el = createCornerHandle();
      const marker = new mapboxgl.Marker({ element: el, draggable: true })
        .setLngLat(coords[i] as [number, number])
        .addTo(map);

      marker.on('dragstart', () => { isDraggingRef.current = true; });
      marker.on('drag', () => {
        const ll = marker.getLngLat();
        coords[i] = [ll.lng, ll.lat];
        repositionAll();
        syncToMapSource();
      });
      marker.on('dragend', () => {
        isDraggingRef.current = false;
        flushToStore();
      });

      cornerMarkersRef.current.push(marker);
    }

    // ═══ 2. SIDE MIDPOINT MARKERS (4) — edge stretching ══════════════════
    // top(0-1)=horizontal, right(1-2)=vertical, bottom(2-3)=horizontal, left(3-0)=vertical
    const sideConfigs = [
      { corners: [0, 1], isHorizontal: true },
      { corners: [1, 2], isHorizontal: false },
      { corners: [2, 3], isHorizontal: true },
      { corners: [3, 0], isHorizontal: false },
    ];

    for (let s = 0; s < 4; s++) {
      const config = sideConfigs[s];
      const mid = getMidpoint(coords[config.corners[0]], coords[config.corners[1]]);
      const el = createSideHandle(config.isHorizontal);
      const marker = new mapboxgl.Marker({ element: el, draggable: true })
        .setLngLat(mid)
        .addTo(map);

      let lastPos = { lng: mid[0], lat: mid[1] };

      marker.on('dragstart', () => {
        isDraggingRef.current = true;
        const ll = marker.getLngLat();
        lastPos = { lng: ll.lng, lat: ll.lat };
      });

      marker.on('drag', () => {
        const ll = marker.getLngLat();

        if (config.isHorizontal) {
          // Top/bottom edge: only move vertically (lat)
          const dLat = ll.lat - lastPos.lat;
          coords[config.corners[0]][1] += dLat;
          coords[config.corners[1]][1] += dLat;
        } else {
          // Left/right edge: only move horizontally (lng)
          const dLng = ll.lng - lastPos.lng;
          coords[config.corners[0]][0] += dLng;
          coords[config.corners[1]][0] += dLng;
        }

        lastPos = { lng: ll.lng, lat: ll.lat };
        repositionAll();
        syncToMapSource();
      });

      marker.on('dragend', () => {
        isDraggingRef.current = false;
        flushToStore();
      });

      sideMarkersRef.current.push(marker);
    }

    // ═══ 3. SCALE MARKERS (4) — proportional scaling from center ═════════
    let scaleStart: {
      centerLng: number;
      centerLat: number;
      originalCoords: number[][];
      originalDist: number;
    } | null = null;

    for (let i = 0; i < 4; i++) {
      const pos = getScaleHandlePos(i);
      const el = createScaleHandle();
      const marker = new mapboxgl.Marker({ element: el, draggable: true })
        .setLngLat(pos)
        .addTo(map);

      marker.on('dragstart', () => {
        isDraggingRef.current = true;
        const c = getCenter(coords);
        const handlePos = getScaleHandlePos(i);
        const dx = handlePos[0] - c[0];
        const dy = handlePos[1] - c[1];
        scaleStart = {
          centerLng: c[0],
          centerLat: c[1],
          originalCoords: coords.map((pt) => [...pt]),
          originalDist: Math.sqrt(dx * dx + dy * dy),
        };
      });

      marker.on('drag', () => {
        if (!scaleStart || scaleStart.originalDist === 0) return;

        const ll = marker.getLngLat();
        const dx = ll.lng - scaleStart.centerLng;
        const dy = ll.lat - scaleStart.centerLat;
        const newDist = Math.sqrt(dx * dx + dy * dy);
        const scale = newDist / scaleStart.originalDist;

        for (let j = 0; j < 4; j++) {
          const ox = scaleStart.originalCoords[j][0] - scaleStart.centerLng;
          const oy = scaleStart.originalCoords[j][1] - scaleStart.centerLat;
          coords[j][0] = scaleStart.centerLng + ox * scale;
          coords[j][1] = scaleStart.centerLat + oy * scale;
        }

        repositionAll();
        syncToMapSource();
      });

      marker.on('dragend', () => {
        isDraggingRef.current = false;
        scaleStart = null;
        flushToStore();
      });

      scaleMarkersRef.current.push(marker);
    }

    // ═══ 4. CENTER MARKER (move) ═════════════════════════════════════════
    const centerEl = createCenterHandle();
    const centerPos = getCenter(coords);
    const centerMarker = new mapboxgl.Marker({ element: centerEl, draggable: true })
      .setLngLat(centerPos)
      .addTo(map);

    let lastCenter = { lng: centerPos[0], lat: centerPos[1] };

    centerMarker.on('dragstart', () => {
      isDraggingRef.current = true;
      const ll = centerMarker.getLngLat();
      lastCenter = { lng: ll.lng, lat: ll.lat };
    });

    centerMarker.on('drag', () => {
      const ll = centerMarker.getLngLat();
      const dLng = ll.lng - lastCenter.lng;
      const dLat = ll.lat - lastCenter.lat;

      for (let i = 0; i < 4; i++) {
        coords[i][0] += dLng;
        coords[i][1] += dLat;
      }
      lastCenter = { lng: ll.lng, lat: ll.lat };

      // Reposition all markers (center is being dragged)
      for (let i = 0; i < cornerMarkersRef.current.length; i++) {
        cornerMarkersRef.current[i].setLngLat(coords[i] as [number, number]);
      }
      for (let i = 0; i < sideMarkersRef.current.length; i++) {
        const mid = getMidpoint(coords[sideEdges[i][0]], coords[sideEdges[i][1]]);
        sideMarkersRef.current[i].setLngLat(mid);
      }
      for (let i = 0; i < scaleMarkersRef.current.length; i++) {
        scaleMarkersRef.current[i].setLngLat(getScaleHandlePos(i));
      }
      updateRotationHandlePosition();
      syncToMapSource();
    });

    centerMarker.on('dragend', () => {
      isDraggingRef.current = false;
      flushToStore();
    });

    centerMarkerRef.current = centerMarker;

    // ═══ 6. ROTATION HANDLE (green circle with dashed line from center) ═══
    const rotHandlePos = getRotationHandlePos();

    map.addSource('overlay-rotation-line-source', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [centerPos, rotHandlePos] },
        properties: {},
      },
    });

    map.addLayer({
      id: 'overlay-rotation-line-layer',
      type: 'line',
      source: 'overlay-rotation-line-source',
      paint: {
        'line-color': '#ff00ff',
        'line-width': 2,
        'line-dasharray': [3, 3],
      },
    });
    rotationLineRef.current = true;

    const rotateEl = createRotationHandle();
    const rotationMarker = new mapboxgl.Marker({ element: rotateEl, draggable: true })
      .setLngLat(rotHandlePos)
      .addTo(map);

    let rotationStart: {
      centerLng: number;
      centerLat: number;
      startAngle: number;
      originalCoords: number[][];
      lngScale: number;
    } | null = null;

    rotationMarker.on('dragstart', () => {
      isDraggingRef.current = true;
      rotateEl.style.cursor = 'grabbing';
      const c = getCenter(coords);
      const hll = rotationMarker.getLngLat();
      // cos(lat) correction: 1° lng ≠ 1° lat in physical distance
      const lngScale = Math.cos(c[1] * Math.PI / 180);
      rotationStart = {
        centerLng: c[0],
        centerLat: c[1],
        startAngle: Math.atan2(hll.lat - c[1], (hll.lng - c[0]) * lngScale),
        originalCoords: coords.map((pt) => [...pt]),
        lngScale,
      };
    });

    rotationMarker.on('drag', () => {
      if (!rotationStart) return;

      const hll = rotationMarker.getLngLat();
      const lngScale = rotationStart.lngScale;
      const currentAngle = Math.atan2(
        hll.lat - rotationStart.centerLat,
        (hll.lng - rotationStart.centerLng) * lngScale
      );
      const deltaAngle = currentAngle - rotationStart.startAngle;
      const cos = Math.cos(deltaAngle);
      const sin = Math.sin(deltaAngle);

      for (let i = 0; i < 4; i++) {
        // Normalize lng to equal-distance space, rotate, then convert back
        const dx = (rotationStart.originalCoords[i][0] - rotationStart.centerLng) * lngScale;
        const dy = rotationStart.originalCoords[i][1] - rotationStart.centerLat;
        const rx = dx * cos - dy * sin;
        const ry = dx * sin + dy * cos;
        coords[i][0] = rotationStart.centerLng + rx / lngScale;
        coords[i][1] = rotationStart.centerLat + ry;
      }

      // Reposition all other markers
      for (let i = 0; i < cornerMarkersRef.current.length; i++) {
        cornerMarkersRef.current[i].setLngLat(coords[i] as [number, number]);
      }
      for (let i = 0; i < sideMarkersRef.current.length; i++) {
        const mid = getMidpoint(coords[sideEdges[i][0]], coords[sideEdges[i][1]]);
        sideMarkersRef.current[i].setLngLat(mid);
      }
      for (let i = 0; i < scaleMarkersRef.current.length; i++) {
        scaleMarkersRef.current[i].setLngLat(getScaleHandlePos(i));
      }
      if (centerMarkerRef.current) {
        centerMarkerRef.current.setLngLat(getCenter(coords));
      }
      // Update rotation line
      const lineSource = map.getSource('overlay-rotation-line-source') as mapboxgl.GeoJSONSource | undefined;
      if (lineSource) {
        lineSource.setData({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [getCenter(coords), [hll.lng, hll.lat]] },
          properties: {},
        });
      }

      syncToMapSource();
    });

    rotationMarker.on('dragend', () => {
      isDraggingRef.current = false;
      rotateEl.style.cursor = 'grab';
      rotationStart = null;
      flushToStore();
    });

    rotationMarkerRef.current = rotationMarker;

    return cleanup;
  }, [activeOverlayId, overlayAdjustMode, isReadOnly]);
}
